import React, { useRef, useState } from "react";
import { X, Download, Wrench, Upload, Loader2, Check, AlertTriangle, Feather } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import * as api from "../api";

// Carica lo zip a blocchi (stesso meccanismo degli allegati) e ritorna l'upload_id
async function caricaZip(file, onProgress) {
  const start = await axios.post(`${api.API}/uploads/start`, { nome: file.name, tipo: "application/zip", size: file.size }).then((r) => r.data);
  const size = start.chunk_size || 600 * 1024;
  let index = 0;
  for (let off = 0; off < file.size; off += size, index++) {
    await axios.post(`${api.API}/uploads/${start.upload_id}/chunk`, file.slice(off, off + size), { params: { index }, headers: { "Content-Type": "application/octet-stream" } });
    onProgress(Math.min(1, (off + size) / file.size));
  }
  await axios.post(`${api.API}/uploads/${start.upload_id}/finish`);
  return start.upload_id;
}

function Card({ icon: Icon, colore, titolo, testo, children, testid }) {
  return (
    <div className="manu-card" data-testid={testid}>
      <div className="manu-icon" style={{ color: colore }}><Icon size={34} strokeWidth={2.2} /></div>
      <h3 className="manu-title">{titolo}</h3>
      <p className="manu-text">{testo}</p>
      {children}
    </div>
  );
}

// Impostazioni → Manutenzione (solo admin): backup senza dati, ottimizzazione DB, ripristino
export default function ManutenzioneModal({ open, onClose }) {
  const [backupBusy, setBackupBusy] = useState(false);
  const [ottBusy, setOttBusy] = useState(false);
  const [ottEsito, setOttEsito] = useState(null);
  const [file, setFile] = useState(null);
  const [conferma, setConferma] = useState("");
  const [ripBusy, setRipBusy] = useState(false);
  const [ripProg, setRipProg] = useState(0);
  const [ripEsito, setRipEsito] = useState(null);
  const [allBusy, setAllBusy] = useState(false);
  const [allEsito, setAllEsito] = useState(null);
  const fileRef = useRef(null);
  if (!open) return null;

  const generaBackup = async () => {
    setBackupBusy(true);
    const t = toast.loading("Genero il backup (Front-End + Back-End + struttura DB)...");
    try {
      const r = await axios.get(`${api.API}/manutenzione/backup`, { responseType: "blob", timeout: 300000 });
      const cd = r.headers["content-disposition"] || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] || "BKP_PostaAI.zip";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 60000);
      toast.success(`Backup pronto: ${nome} (${(r.data.size / 1e6).toFixed(1)} MB)`, { id: t });
    } catch (e) {
      toast.error(`Backup non riuscito: ${e?.response?.data?.detail || e.message}`, { id: t });
    } finally {
      setBackupBusy(false);
    }
  };

  const ottimizza = async () => {
    setOttBusy(true);
    setOttEsito(null);
    const t = toast.loading("Ottimizzo il database...");
    try {
      const r = await axios.post(`${api.API}/manutenzione/ottimizza`, null, { timeout: 600000 }).then((x) => x.data);
      setOttEsito(r);
      toast.success(`Database ottimizzato in ${r.durata_s}s`, { id: t });
    } catch (e) {
      toast.error(`Ottimizzazione non riuscita: ${e?.response?.data?.detail || e.message}`, { id: t });
    } finally {
      setOttBusy(false);
    }
  };

  const alleggerisci = async () => {
    setAllBusy(true);
    setAllEsito(null);
    const t = toast.loading("Alleggerisco le caselle...");
    try {
      const r = await axios.post(`${api.API}/manutenzione/alleggerisci`, null, { params: { giorni: 30 }, timeout: 600000 }).then((x) => x.data);
      setAllEsito(r);
      toast.success(`Alleggerite ${r.email} email in ${r.durata_s}s`, { id: t });
    } catch (e) {
      toast.error(`Alleggerimento non riuscito: ${e?.response?.data?.detail || e.message}`, { id: t });
    } finally {
      setAllBusy(false);
    }
  };

  const ripristina = async () => {
    if (!file) return toast.error("Scegli il file di backup (.zip)");
    if (conferma.trim().toUpperCase() !== "RIPRISTINA") return toast.error('Per confermare scrivi "RIPRISTINA"');
    setRipBusy(true);
    setRipEsito(null);
    const t = toast.loading("Carico il backup...");
    try {
      const uploadId = await caricaZip(file, setRipProg);
      toast.loading("Ripristino in corso...", { id: t });
      const r = await axios.post(`${api.API}/manutenzione/ripristina`, { upload_id: uploadId, conferma: "RIPRISTINA" }, { timeout: 900000 }).then((x) => x.data);
      setRipEsito(r);
      toast.success(`Ripristino completato: ${r.modalita}`, { id: t });
      setFile(null);
      setConferma("");
    } catch (e) {
      toast.error(`Ripristino non riuscito: ${e?.response?.data?.detail || e.message}`, { id: t });
    } finally {
      setRipBusy(false);
      setRipProg(0);
    }
  };

  return (
    <div className="wf-modal-backdrop show" onClick={onClose} data-testid="manutenzione-backdrop">
      <div className="wf-modal manu-modal" onClick={(e) => e.stopPropagation()} data-testid="manutenzione-modal">
        <div className="wf-modal-head">
          <div>
            <div className="manu-kicker">Pannello admin</div>
            <div className="wf-modal-title manu-h">Manutenzione</div>
            <div className="manu-sub">Backup e ottimizzazione del database.</div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Chiudi" data-testid="manutenzione-close"><X size={20} /></button>
        </div>

        <div className="manu-grid">
          <Card icon={Download} colore="#2fbf71" titolo="Genera backup" testid="manu-backup"
            testo='Backup completo del codice (Front-End, Back-End) e della struttura del database (collezioni e indici), SENZA dati, scaricabile come .zip: es. BKP_PostaAI_140926-1750.zip.'>
            <label className="manu-label">Destinazione</label>
            <select className="wf-input manu-select" defaultValue="postaai" data-testid="manu-backup-dest">
              <option value="postaai">POSTA AI</option>
            </select>
            <button className="wf-btn primary manu-btn" onClick={generaBackup} disabled={backupBusy} data-testid="manu-backup-btn">
              {backupBusy ? <Loader2 size={16} className="spin" /> : null} Genera backup
            </button>
          </Card>

          <Card icon={Wrench} colore="#2196e8" titolo="Ottimizza database" testid="manu-ottimizza"
            testo="Elimina allegati orfani, upload scaduti e vecchi errori, poi verifica e crea gli indici mancanti (createIndex, senza bloccare il database). Compatibile con MongoDB Atlas.">
            <label className="manu-label">Database</label>
            <select className="wf-input manu-select" defaultValue="postaai" data-testid="manu-ott-db">
              <option value="postaai">POSTA AI</option>
            </select>
            <button className="wf-btn primary manu-btn" onClick={ottimizza} disabled={ottBusy} data-testid="manu-ott-btn">
              {ottBusy ? <Loader2 size={16} className="spin" /> : null} Ottimizza database
            </button>
            {ottEsito && (
              <div className="manu-esito" data-testid="manu-ott-esito">
                <div><Check size={14} /> Disco: {ottEsito.prima.disco_mb} → {ottEsito.dopo.disco_mb} MB · Dati: {ottEsito.prima.dati_mb} → {ottEsito.dopo.dati_mb} MB · Indici: {ottEsito.prima.indici_mb} → {ottEsito.dopo.indici_mb} MB</div>
                <div>Indici: {ottEsito.indici_presenti} già presenti{ottEsito.indici_creati?.length ? `, ${ottEsito.indici_creati.length} creati (${ottEsito.indici_creati.join(", ")})` : ", nessuno da creare"}{Object.keys(ottEsito.pulizia || {}).length ? ` · pulizia: ${Object.entries(ottEsito.pulizia).map(([k, v]) => `${k} ${v}`).join(", ")}` : ""}</div>
                {ottEsito.avvisi?.length > 0 && <div className="manu-avviso"><AlertTriangle size={13} /> {ottEsito.avvisi.join(" · ")}</div>}
              </div>
            )}
          </Card>

          <Card icon={Feather} colore="#8e44ad" titolo="Alleggerisci caselle" testid="manu-alleggerisci"
            testo="Svuota il corpo e gli allegati in cache delle email più vecchie di 30 giorni già importate (tutte le caselle IMAP). Restano mittente, oggetto, data e anteprima: il contenuto si riscarica da solo alla prima apertura.">
            <button className="wf-btn primary manu-btn" onClick={alleggerisci} disabled={allBusy} data-testid="manu-all-btn">
              {allBusy ? <Loader2 size={16} className="spin" /> : null} Alleggerisci caselle
            </button>
            {allEsito && (
              <div className="manu-esito" data-testid="manu-all-esito">
                <div><Check size={14} /> {allEsito.email} email alleggerite · {allEsito.allegati} allegati in cache rimossi · {allEsito.durata_s}s</div>
              </div>
            )}
          </Card>

          <Card icon={Upload} colore="#f2c230" titolo="Ripristina backup" testid="manu-ripristina"
            testo="Carica un file di backup (.zip) generato dall'app. Struttura → ricrea collezioni e indici; dump dati → le collezioni contenute vengono sostituite.">
            <label className="manu-label">Destinazione</label>
            <select className="wf-input manu-select" defaultValue="postaai" data-testid="manu-rip-dest">
              <option value="postaai">POSTA AI</option>
            </select>
            <label className="manu-label">File di backup</label>
            <div className="manu-file">
              <button className="wf-btn" type="button" onClick={() => fileRef.current?.click()} data-testid="manu-rip-scegli">Scegli file</button>
              <span className="manu-file-name">{file ? `${file.name} (${(file.size / 1e6).toFixed(1)} MB)` : "Nessun file selezionato"}</span>
              <input ref={fileRef} type="file" accept=".zip,application/zip" style={{ display: "none" }} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="manu-rip-file" />
            </div>
            <label className="manu-label">Conferma (scrivi RIPRISTINA)</label>
            <input className="wf-input" value={conferma} onChange={(e) => setConferma(e.target.value)} placeholder="RIPRISTINA" data-testid="manu-rip-conferma" />
            <button className="wf-btn manu-btn manu-btn-warn" onClick={ripristina} disabled={ripBusy || !file} data-testid="manu-rip-btn">
              {ripBusy ? <><Loader2 size={16} className="spin" /> {ripProg > 0 && ripProg < 1 ? `${Math.round(ripProg * 100)}%` : ""}</> : null} Ripristina backup
            </button>
            {ripEsito && (
              <div className="manu-esito" data-testid="manu-rip-esito">
                <div><Check size={14} /> {ripEsito.modalita}</div>
                <div>Collezioni: {ripEsito.collezioni.join(", ") || "—"}{ripEsito.indici ? ` · indici: ${ripEsito.indici}` : ""}</div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
